/**
 * Simple Dialogs for Practice
 * Basic conversations for kids
 */

export interface Dialog {
  id: string;
  title: string;
  scene: string;
  level: 'beginner' | 'intermediate' | 'advanced'; // Add advanced level for middle school
  lines: Array<{
    speaker: string;
    text: string;
    translation?: string;
  }>;
  keywords: string[];
}

export const dialogs: Dialog[] = [
  {
    id: 'greeting_1',
    title: '打招呼',
    scene: '学校',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'Hello!', translation: '你好！' },
      { speaker: 'B', text: 'Hi! How are you?', translation: '嗨！你好吗？' },
      { speaker: 'A', text: "I'm fine, thank you!", translation: '我很好，谢谢！' },
      { speaker: 'B', text: 'Nice to meet you!', translation: '很高兴见到你！' },
    ],
    keywords: ['hello', 'hi', 'how are you', 'fine', 'thank you'],
  },
  {
    id: 'introduction_1',
    title: '自我介绍',
    scene: '学校',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: "What's your name?", translation: '你叫什么名字？' },
      { speaker: 'B', text: 'My name is Tom. What about you?', translation: '我叫Tom。你呢？' },
      { speaker: 'A', text: 'I am Amy. How old are you?', translation: '我是Amy。你几岁了？' },
      { speaker: 'B', text: "I'm seven years old.", translation: '我七岁了。' },
    ],
    keywords: ['name', 'old', 'years'],
  },
  {
    id: 'favorite_color',
    title: '喜欢的颜色',
    scene: '教室',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'What is your favorite color?', translation: '你最喜欢什么颜色？' },
      { speaker: 'B', text: 'I like blue. What about you?', translation: '我喜欢蓝色。你呢？' },
      { speaker: 'A', text: 'My favorite color is red!', translation: '我最喜欢的颜色是红色！' },
      { speaker: 'B', text: 'Red is a nice color!', translation: '红色是个好看的颜色！' },
    ],
    keywords: ['color', 'favorite', 'like'],
  },
  {
    id: 'daily_routine',
    title: '日常生活',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'Wake up, it is morning!', translation: '起床了，早上好！' },
      { speaker: 'Child', text: 'Good morning, Mom!', translation: '早上好，妈妈！' },
      { speaker: 'Mom', text: 'Time to eat breakfast.', translation: '该吃早饭了。' },
      { speaker: 'Child', text: 'I want bread and milk!', translation: '我想要面包和牛奶！' },
    ],
    keywords: ['wake up', 'morning', 'breakfast', 'eat'],
  },
  {
    id: 'at_school',
    title: '在学校',
    scene: '学校',
    level: 'intermediate',
    lines: [
      { speaker: 'Teacher', text: 'Good morning, class!', translation: '早上好，同学们！' },
      { speaker: 'Students', text: 'Good morning, teacher!', translation: '早上好，老师！' },
      { speaker: 'Teacher', text: 'Open your books, please.', translation: '请打开你们的书。' },
      { speaker: 'Student', text: 'Okay, teacher!', translation: '好的，老师！' },
    ],
    keywords: ['good morning', 'class', 'teacher', 'book'],
  },
  {
    id: 'shopping',
    title: '买东西',
    scene: '商店',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Can I help you?', translation: '需要帮忙吗？' },
      { speaker: 'B', text: 'Yes, please. I want an apple.', translation: '是的，我想要一个苹果。' },
      { speaker: 'A', text: 'Here you are!', translation: '给你！' },
      { speaker: 'B', text: 'Thank you very much!', translation: '非常感谢！' },
    ],
    keywords: ['help', 'want', 'please', 'thank you'],
  },
  {
    id: 'playing',
    title: '玩游戏',
    scene: '公园',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'Do you want to play?', translation: '你想玩吗？' },
      { speaker: 'B', text: 'Yes! What game?', translation: '想！什么游戏？' },
      { speaker: 'A', text: "Let's play tag!", translation: '我们来玩抓人游戏！' },
      { speaker: 'B', text: 'Great idea! Run!', translation: '好主意！跑！' },
    ],
    keywords: ['play', 'game', 'run', 'idea'],
  },
  {
    id: 'weather',
    title: '谈论天气',
    scene: '户外',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'What is the weather today?', translation: '今天天气怎么样？' },
      { speaker: 'B', text: 'It is sunny and warm!', translation: '晴朗又温暖！' },
      { speaker: 'A', text: "Let's go outside!", translation: '我们出去吧！' },
      { speaker: 'B', text: 'Good idea!', translation: '好主意！' },
    ],
    keywords: ['weather', 'sunny', 'warm', 'outside'],
  },
  {
    id: 'food',
    title: '喜欢的食物',
    scene: '家里',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'What do you want to eat?', translation: '你想吃什么？' },
      { speaker: 'Child', text: 'I like bananas!', translation: '我喜欢香蕉！' },
      { speaker: 'Mom', text: 'Okay, here is a banana.', translation: '好的，给你香蕉。' },
      { speaker: 'Child', text: 'Thank you, Mom!', translation: '谢谢妈妈！' },
    ],
    keywords: ['eat', 'like', 'banana', 'thank you'],
  },
  {
    id: 'bedtime',
    title: '睡前',
    scene: '卧室',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'It is time for bed.', translation: '该睡觉了。' },
      { speaker: 'Child', text: 'Can you tell a story?', translation: '你能讲故事吗？' },
      { speaker: 'Mom', text: 'Sure! Once upon a time...', translation: '当然！从前...' },
      { speaker: 'Child', text: 'Good night, Mom!', translation: '晚安，妈妈！' },
    ],
    keywords: ['bed', 'story', 'good night', 'time'],
  },

  // Elementary School - Intermediate Level
  {
    id: 'asking_directions',
    title: '问路',
    scene: '街上',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Excuse me, where is the library?', translation: '打扰一下，图书馆在哪里？' },
      { speaker: 'B', text: 'Go straight and turn left.', translation: '直走，然后左转。' },
      { speaker: 'A', text: 'Thank you very much!', translation: '非常感谢！' },
      { speaker: 'B', text: 'You are welcome!', translation: '不客气！' },
    ],
    keywords: ['where', 'library', 'straight', 'turn', 'left'],
  },
  {
    id: 'making_friends',
    title: '交朋友',
    scene: '学校',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Hello! What do you like to do?', translation: '你好！你喜欢做什么？' },
      { speaker: 'B', text: 'I like playing basketball.', translation: '我喜欢打篮球。' },
      { speaker: 'A', text: 'That sounds fun! Can I play with you?', translation: '听起来很有趣！我可以和你一起玩吗？' },
      { speaker: 'B', text: 'Sure! Let us be friends!', translation: '当然！让我们做朋友吧！' },
    ],
    keywords: ['like', 'basketball', 'fun', 'friends', 'play'],
  },
  {
    id: 'at_home',
    title: '在家里',
    scene: '客厅',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'What did you do at school today?', translation: '你今天在学校做了什么？' },
      { speaker: 'Child', text: 'I learned English and math.', translation: '我学了英语和数学。' },
      { speaker: 'Mom', text: 'That is great! Did you finish your homework?', translation: '太棒了！你做完作业了吗？' },
      { speaker: 'Child', text: 'Yes, I finished it.', translation: '是的，我做完了。' },
    ],
    keywords: ['school', 'learn', 'homework', 'finish', 'English', 'math'],
  },
  {
    id: 'shopping_with_parents',
    title: '和父母购物',
    scene: '超市',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'We need to buy some vegetables.', translation: '我们需要买一些蔬菜。' },
      { speaker: 'Child', text: 'Can I help you?', translation: '我可以帮你吗？' },
      { speaker: 'Mom', text: 'Yes, please get some tomatoes.', translation: '好的，请拿一些西红柿。' },
      { speaker: 'Child', text: 'Here are the tomatoes!', translation: '西红柿在这里！' },
    ],
    keywords: ['buy', 'vegetables', 'help', 'tomatoes', 'shopping'],
  },
  {
    id: 'weekend_plans',
    title: '周末计划',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'Dad', text: 'What do you want to do this weekend?', translation: '这个周末你想做什么？' },
      { speaker: 'Child', text: 'I want to go to the park!', translation: '我想去公园！' },
      { speaker: 'Dad', text: 'Good idea! We can play there.', translation: '好主意！我们可以在那里玩。' },
      { speaker: 'Child', text: 'I am so excited!', translation: '我好兴奋！' },
    ],
    keywords: ['weekend', 'want', 'park', 'play', 'excited'],
  },

  // Middle School - Advanced Level
  {
    id: 'discussing_hobbies',
    title: '讨论爱好',
    scene: '学校',
    level: 'advanced',
    lines: [
      { speaker: 'A', text: 'What are your hobbies?', translation: '你的爱好是什么？' },
      { speaker: 'B', text: 'I enjoy reading books and playing guitar.', translation: '我喜欢读书和弹吉他。' },
      { speaker: 'A', text: 'That is interesting! How long have you played guitar?', translation: '真有趣！你弹吉他多久了？' },
      { speaker: 'B', text: 'I have played for three years.', translation: '我弹了三年了。' },
    ],
    keywords: ['hobbies', 'enjoy', 'reading', 'guitar', 'interesting', 'years'],
  },
  {
    id: 'planning_vacation',
    title: '计划假期',
    scene: '家里',
    level: 'advanced',
    lines: [
      { speaker: 'Mom', text: 'Where should we go for vacation?', translation: '我们应该去哪里度假？' },
      { speaker: 'Child', text: 'I want to visit Beijing!', translation: '我想去北京！' },
      { speaker: 'Mom', text: 'Beijing is a great city. What do you want to see?', translation: '北京是个很棒的城市。你想看什么？' },
      { speaker: 'Child', text: 'I want to visit the Great Wall and the Forbidden City.', translation: '我想参观长城和故宫。' },
    ],
    keywords: ['vacation', 'visit', 'Beijing', 'Great Wall', 'Forbidden City'],
  },
  {
    id: 'science_class',
    title: '科学课',
    scene: '教室',
    level: 'advanced',
    lines: [
      { speaker: 'Teacher', text: 'Today we will learn about plants.', translation: '今天我们将学习植物。' },
      { speaker: 'Student', text: 'What do plants need to grow?', translation: '植物生长需要什么？' },
      { speaker: 'Teacher', text: 'Plants need water, sunlight, and air.', translation: '植物需要水、阳光和空气。' },
      { speaker: 'Student', text: 'I understand! Plants are amazing.', translation: '我明白了！植物真神奇。' },
    ],
    keywords: ['learn', 'plants', 'grow', 'water', 'sunlight', 'air'],
  },
  {
    id: 'future_career',
    title: '未来的职业',
    scene: '学校',
    level: 'advanced',
    lines: [
      { speaker: 'A', text: 'What do you want to be in the future?', translation: '你未来想做什么？' },
      { speaker: 'B', text: 'I want to be an engineer.', translation: '我想当一名工程师。' },
      { speaker: 'A', text: 'Why do you want to be an engineer?', translation: '为什么你想当工程师？' },
      { speaker: 'B', text: 'Because I like building things and solving problems.', translation: '因为我喜欢建造东西和解决问题。' },
    ],
    keywords: ['future', 'engineer', 'building', 'solving', 'problems'],
  },
  {
    id: 'protecting_environment',
    title: '保护环境',
    scene: '公园',
    level: 'advanced',
    lines: [
      { speaker: 'A', text: 'Why should we protect the environment?', translation: '为什么我们要保护环境？' },
      { speaker: 'B', text: 'Because pollution harms animals and plants.', translation: '因为污染伤害动物和植物。' },
      { speaker: 'A', text: 'What can we do to help?', translation: '我们可以做什么来帮助？' },
      { speaker: 'B', text: 'We can recycle and use less plastic.', translation: '我们可以回收利用并少用塑料。' },
    ],
    keywords: ['environment', 'protect', 'pollution', 'recycle', 'plastic'],
  },

  // ==================== More School Life Scenes ====================
  {
    id: 'in_the_classroom',
    title: '在教室里',
    scene: '教室',
    level: 'beginner',
    lines: [
      { speaker: 'Teacher', text: 'Please open your books to page ten.', translation: '请把书翻到第10页。' },
      { speaker: 'Student', text: 'Okay, teacher.', translation: '好的，老师。' },
      { speaker: 'Teacher', text: 'Who can read the first paragraph?', translation: '谁能读第一段？' },
      { speaker: 'Student', text: 'I can!', translation: '我可以！' },
    ],
    keywords: ['book', 'page', 'read', 'paragraph'],
  },
  {
    id: 'at_the_cafeteria',
    title: '在食堂',
    scene: '学校食堂',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'What do you want for lunch?', translation: '午餐你想吃什么？' },
      { speaker: 'B', text: 'I want rice and vegetables.', translation: '我想要米饭和蔬菜。' },
      { speaker: 'A', text: 'Do you want some soup?', translation: '你想要汤吗？' },
      { speaker: 'B', text: 'Yes, please!', translation: '好的，谢谢！' },
    ],
    keywords: ['lunch', 'rice', 'vegetables', 'soup'],
  },
  {
    id: 'borrowing_books',
    title: '借书',
    scene: '图书馆',
    level: 'intermediate',
    lines: [
      { speaker: 'Student', text: 'Excuse me, can I borrow this book?', translation: '打扰一下，我可以借这本书吗？' },
      { speaker: 'Librarian', text: 'Sure! Do you have your library card?', translation: '当然！你有借书卡吗？' },
      { speaker: 'Student', text: 'Yes, here it is.', translation: '有，在这里。' },
      { speaker: 'Librarian', text: 'Please return it in two weeks.', translation: '请在两周内归还。' },
    ],
    keywords: ['borrow', 'book', 'library card', 'return', 'weeks'],
  },
  {
    id: 'school_bus',
    title: '坐校车',
    scene: '校车上',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'Here comes the school bus!', translation: '校车来了！' },
      { speaker: 'Child', text: 'Goodbye, Mom!', translation: '再见，妈妈！' },
      { speaker: 'Driver', text: 'Good morning! Hop on!', translation: '早上好！上车吧！' },
      { speaker: 'Child', text: 'Thank you!', translation: '谢谢！' },
    ],
    keywords: ['school bus', 'goodbye', 'morning', 'hop on'],
  },
  {
    id: 'during_recess',
    title: '课间休息',
    scene: '操场',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'What did the teacher say?', translation: '老师说了什么？' },
      { speaker: 'B', text: 'We have homework today.', translation: '我们今天有作业。' },
      { speaker: 'A', text: 'Can we play for a while?', translation: '我们可以玩一会儿吗？' },
      { speaker: 'B', text: 'Sure, let us play!', translation: '当然，让我们玩吧！' },
    ],
    keywords: ['teacher', 'homework', 'play', 'while'],
  },

  // ==================== Home Life Scenes ====================
  {
    id: 'helping_with_chores',
    title: '帮忙做家务',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'Can you help me clean the room?', translation: '你能帮我打扫房间吗？' },
      { speaker: 'Child', text: 'Sure! What should I do?', translation: '当然！我该做什么？' },
      { speaker: 'Mom', text: 'Please put away your toys.', translation: '请把你的玩具收好。' },
      { speaker: 'Child', text: 'Okay, I will do it now.', translation: '好的，我现在就做。' },
    ],
    keywords: ['help', 'clean', 'room', 'toys', 'put away'],
  },
  {
    id: 'watching_tv',
    title: '看电视',
    scene: '客厅',
    level: 'beginner',
    lines: [
      { speaker: 'Child', text: 'Mom, can I watch TV?', translation: '妈妈，我可以看电视吗？' },
      { speaker: 'Mom', text: 'Only for 30 minutes.', translation: '只能看30分钟。' },
      { speaker: 'Child', text: 'Okay! I want to watch cartoons.', translation: '好的！我想看动画片。' },
      { speaker: 'Mom', text: 'Remember to do your homework later.', translation: '记得待会儿做作业。' },
    ],
    keywords: ['watch', 'TV', 'minutes', 'cartoons', 'homework'],
  },
  {
    id: 'doing_homework',
    title: '做作业',
    scene: '书房',
    level: 'intermediate',
    lines: [
      { speaker: 'Child', text: 'Dad, I need help with my homework.', translation: '爸爸，我需要作业帮助。' },
      { speaker: 'Dad', text: 'Sure, what subject?', translation: '当然，什么科目？' },
      { speaker: 'Child', text: 'Math. I do not understand this problem.', translation: '数学。我不明白这道题。' },
      { speaker: 'Dad', text: 'Let me take a look.', translation: '让我看看。' },
    ],
    keywords: ['homework', 'help', 'subject', 'math', 'problem'],
  },
  {
    id: 'setting_the_table',
    title: '摆餐桌',
    scene: '餐厅',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'Dinner is ready!', translation: '晚饭好了！' },
      { speaker: 'Child', text: 'Can I help set the table?', translation: '我可以帮忙摆餐桌吗？' },
      { speaker: 'Mom', text: 'Yes, please put the spoons on the table.', translation: '好的，请把勺子放在桌上。' },
      { speaker: 'Child', text: 'Done! Can we eat now?', translation: '完成了！我们可以吃了吗？' },
    ],
    keywords: ['dinner', 'ready', 'set table', 'spoons', 'eat'],
  },
  {
    id: 'before_sleep',
    title: '睡前准备',
    scene: '卧室',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'It is time to go to bed.', translation: '该睡觉了。' },
      { speaker: 'Child', text: 'But I am not sleepy yet.', translation: '但我还不困。' },
      { speaker: 'Mom', text: 'You need to sleep early for school tomorrow.', translation: '你需要早点睡，明天还要上学。' },
      { speaker: 'Child', text: 'Okay, good night, Mom.', translation: '好的，晚安，妈妈。' },
    ],
    keywords: ['time', 'bed', 'sleepy', 'early', 'school'],
  },

  // ==================== Social Scenes ====================
  {
    id: 'birthday_party',
    title: '生日派对',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Happy Birthday! Here is a gift for you!', translation: '生日快乐！这是给你的礼物！' },
      { speaker: 'B', text: 'Thank you so much! Can I open it?', translation: '非常感谢！我可以打开吗？' },
      { speaker: 'A', text: 'Sure! I hope you like it.', translation: '当然！希望你喜欢。' },
      { speaker: 'B', text: 'Wow! I love it! Thank you!', translation: '哇！我很喜欢！谢谢！' },
    ],
    keywords: ['birthday', 'gift', 'open', 'like', 'love'],
  },
  {
    id: 'phone_call',
    title: '打电话',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Hello! May I speak to Tom?', translation: '你好！我可以和Tom说话吗？' },
      { speaker: 'B', text: 'This is Tom speaking. Who is this?', translation: '我是Tom。请问你是谁？' },
      { speaker: 'A', text: 'It is Amy. Are you free this afternoon?', translation: '我是Amy。你今天下午有空吗？' },
      { speaker: 'B', text: 'Yes, I am free. What do you want to do?', translation: '是的，我有空。你想做什么？' },
    ],
    keywords: ['hello', 'speak', 'free', 'afternoon'],
  },
  {
    id: 'visiting_friend',
    title: '看望朋友',
    scene: '朋友家',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Come in! Nice to see you!', translation: '请进！很高兴见到你！' },
      { speaker: 'B', text: 'Nice to see you too! How are you?', translation: '我也很高兴见到你！你好吗？' },
      { speaker: 'A', text: 'I am fine. Would you like some juice?', translation: '我很好。你想喝果汁吗？' },
      { speaker: 'B', text: 'Yes, please! Thank you!', translation: '好的，谢谢！' },
    ],
    keywords: ['come in', 'nice', 'see', 'juice', 'please'],
  },
  {
    id: 'apologizing',
    title: '道歉',
    scene: '学校',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'I am sorry I broke your pencil.', translation: '对不起，我弄坏了你的铅笔。' },
      { speaker: 'B', text: 'It is okay. It was an accident.', translation: '没关系。这是个意外。' },
      { speaker: 'A', text: 'I will buy you a new one.', translation: '我会给你买支新的。' },
      { speaker: 'B', text: 'That is very kind of you!', translation: '你真好！' },
    ],
    keywords: ['sorry', 'broke', 'pencil', 'accident', 'new'],
  },
  {
    id: 'sharing_toys',
    title: '分享玩具',
    scene: '公园',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'Can I play with your toy car?', translation: '我可以玩你的玩具车吗？' },
      { speaker: 'B', text: 'Sure! Here you are.', translation: '当然！给你。' },
      { speaker: 'A', text: 'Thank you! Do you want to play together?', translation: '谢谢！你想一起玩吗？' },
      { speaker: 'B', text: 'Yes! Let us play!', translation: '好的！让我们一起玩！' },
    ],
    keywords: ['play', 'toy', 'car', 'together'],
  },

  // ==================== Shopping Scenes ====================
  {
    id: 'bookstore',
    title: '在书店',
    scene: '书店',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Excuse me, where can I find English books?', translation: '打扰一下，英语书在哪里？' },
      { speaker: 'B', text: 'They are on the second shelf over there.', translation: '在那边的第二个书架上。' },
      { speaker: 'A', text: 'Thank you! How much is this book?', translation: '谢谢！这本书多少钱？' },
      { speaker: 'B', text: 'It is twenty yuan.', translation: '二十元。' },
    ],
    keywords: ['excuse', 'find', 'shelf', 'how much', 'yuan'],
  },
  {
    id: 'stationery_shop',
    title: '文具店',
    scene: '文具店',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'I want to buy some pencils.', translation: '我想买一些铅笔。' },
      { speaker: 'B', text: 'We have many colors. Which do you like?', translation: '我们有很多颜色。你喜欢哪个？' },
      { speaker: 'A', text: 'I like the blue ones.', translation: '我喜欢蓝色的。' },
      { speaker: 'B', text: 'Here you are! Anything else?', translation: '给你！还要别的吗？' },
    ],
    keywords: ['buy', 'pencils', 'colors', 'blue', 'else'],
  },
  {
    id: 'toy_store',
    title: '玩具店',
    scene: '玩具店',
    level: 'intermediate',
    lines: [
      { speaker: 'Child', text: 'Look at this robot! It is so cool!', translation: '看这个机器人！太酷了！' },
      { speaker: 'Dad', text: 'Do you really want it?', translation: '你真的想要吗？' },
      { speaker: 'Child', text: 'Yes, please! I will take good care of it.', translation: '是的，拜托！我会好好爱护它的。' },
      { speaker: 'Dad', text: 'Okay, let us buy it.', translation: '好的，我们买吧。' },
    ],
    keywords: ['look', 'robot', 'cool', 'want', 'buy', 'take care'],
  },
  {
    id: 'fruit_market',
    title: '水果市场',
    scene: '市场',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'These apples look fresh!', translation: '这些苹果看起来很新鲜！' },
      { speaker: 'B', text: 'Yes, they are. How many do you want?', translation: '是的，很新鲜。你想要多少？' },
      { speaker: 'A', text: 'I will take five apples.', translation: '我要五个苹果。' },
      { speaker: 'B', text: 'Here you are! That is ten yuan.', translation: '给你！一共十元。' },
    ],
    keywords: ['apples', 'fresh', 'many', 'five', 'yuan'],
  },
  {
    id: 'clothes_shop',
    title: '服装店',
    scene: '服装店',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Can I try this shirt?', translation: '我可以试穿这件衬衫吗？' },
      { speaker: 'B', text: 'Sure! The fitting room is over there.', translation: '当然！试衣间在那边。' },
      { speaker: 'A', text: 'It is a bit small. Do you have a bigger one?', translation: '有点小。有大一点的吗？' },
      { speaker: 'B', text: 'Let me check. Here is a larger size.', translation: '让我看看。这是大一号的。' },
    ],
    keywords: ['try', 'shirt', 'fitting room', 'small', 'bigger', 'size'],
  },

  // ==================== Outdoor Scenes ====================
  {
    id: 'at_the_zoo',
    title: '在动物园',
    scene: '动物园',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Look! There are monkeys!', translation: '看！有猴子！' },
      { speaker: 'B', text: 'They are so cute! What are they eating?', translation: '它们真可爱！它们在吃什么？' },
      { speaker: 'A', text: 'They are eating bananas.', translation: '它们在吃香蕉。' },
      { speaker: 'B', text: 'Let us go see the lions next.', translation: '我们接下来去看狮子吧。' },
    ],
    keywords: ['look', 'monkeys', 'cute', 'eating', 'bananas', 'lions'],
  },
  {
    id: 'museum_visit',
    title: '参观博物馆',
    scene: '博物馆',
    level: 'advanced',
    lines: [
      { speaker: 'Teacher', text: 'Welcome to the museum! Please stay together.', translation: '欢迎来到博物馆！请保持在一起。' },
      { speaker: 'Student', text: 'What is this dinosaur?', translation: '这是什么恐龙？' },
      { speaker: 'Teacher', text: 'This is a T-Rex. It lived millions of years ago.', translation: '这是霸王龙。它生活在数百万年前。' },
      { speaker: 'Student', text: 'That is amazing!', translation: '太神奇了！' },
    ],
    keywords: ['museum', 'stay together', 'dinosaur', 'T-Rex', 'lived', 'amazing'],
  },
  {
    id: 'sports_field',
    title: '运动场',
    scene: '运动场',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Do you want to play soccer?', translation: '你想踢足球吗？' },
      { speaker: 'B', text: 'I am not good at it.', translation: '我不擅长。' },
      { speaker: 'A', text: 'That is okay! I can teach you.', translation: '没关系！我可以教你。' },
      { speaker: 'B', text: 'Great! Let us try!', translation: '太好了！让我们试试！' },
    ],
    keywords: ['play', 'soccer', 'good', 'teach', 'try'],
  },
  {
    id: 'picnic',
    title: '野餐',
    scene: '公园',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'Let us find a good place for our picnic.', translation: '让我们找个好地方野餐。' },
      { speaker: 'Child', text: 'How about under that tree?', translation: '那棵树下怎么样？' },
      { speaker: 'Mom', text: 'Perfect! Let us spread the blanket.', translation: '完美！让我们铺开毯子。' },
      { speaker: 'Child', text: 'I am hungry! Can we eat now?', translation: '我饿了！我们可以吃了吗？' },
    ],
    keywords: ['picnic', 'place', 'tree', 'blanket', 'hungry', 'eat'],
  },
  {
    id: 'swimming_pool',
    title: '游泳池',
    scene: '游泳池',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Can you swim?', translation: '你会游泳吗？' },
      { speaker: 'B', text: 'A little bit. Can you teach me?', translation: '会一点。你能教我吗？' },
      { speaker: 'A', text: 'Sure! First, hold the side of the pool.', translation: '当然！首先，抓住池边。' },
      { speaker: 'B', text: 'Like this?', translation: '像这样吗？' },
    ],
    keywords: ['swim', 'teach', 'hold', 'pool'],
  },

  // ==================== Transportation ====================
  {
    id: 'taking_subway',
    title: '坐地铁',
    scene: '地铁站',
    level: 'advanced',
    lines: [
      { speaker: 'A', text: 'Which line should we take?', translation: '我们应该坐哪条线？' },
      { speaker: 'B', text: 'We need Line 2 to the city center.', translation: '我们需要坐2号线到市中心。' },
      { speaker: 'A', text: 'How many stops is it?', translation: '有多少站？' },
      { speaker: 'B', text: 'It is five stops from here.', translation: '从这里有五站。' },
    ],
    keywords: ['line', 'take', 'city center', 'stops'],
  },
  {
    id: 'riding_bike',
    title: '骑自行车',
    scene: '街道',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Your bike looks nice!', translation: '你的自行车看起来不错！' },
      { speaker: 'B', text: 'Thanks! My dad bought it for me.', translation: '谢谢！我爸爸给我买的。' },
      { speaker: 'A', text: 'Can we ride together?', translation: '我们可以一起骑吗？' },
      { speaker: 'B', text: 'Sure! Let us ride to the park.', translation: '当然！让我们骑到公园。' },
    ],
    keywords: ['bike', 'nice', 'bought', 'ride', 'park'],
  },

  // ==================== Health and Safety ====================
  {
    id: 'seeing_doctor',
    title: '看医生',
    scene: '医院',
    level: 'intermediate',
    lines: [
      { speaker: 'Doctor', text: 'What seems to be the problem?', translation: '有什么问题？' },
      { speaker: 'Child', text: 'I have a headache and feel tired.', translation: '我头疼，感觉很累。' },
      { speaker: 'Doctor', text: 'Let me check your temperature.', translation: '让我检查一下你的体温。' },
      { speaker: 'Child', text: 'Is it serious?', translation: '严重吗？' },
    ],
    keywords: ['doctor', 'problem', 'headache', 'tired', 'temperature', 'serious'],
  },
  {
    id: 'crossing_street',
    title: '过马路',
    scene: '街道',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'Stop! Look both ways before crossing.', translation: '停下！过马路前要看两边。' },
      { speaker: 'Child', text: 'Is it safe now?', translation: '现在安全吗？' },
      { speaker: 'Mom', text: 'Yes, the light is green. Let us go.', translation: '是的，灯是绿的。让我们走吧。' },
      { speaker: 'Child', text: 'Okay, I will hold your hand.', translation: '好的，我会牵着你的手。' },
    ],
    keywords: ['stop', 'look', 'crossing', 'safe', 'green', 'hand'],
  },

  // ==================== Seasons and Holidays ====================
  {
    id: 'spring_festival',
    title: '春节',
    scene: '家里',
    level: 'advanced',
    lines: [
      { speaker: 'A', text: 'Happy New Year! Gong Xi Fa Cai!', translation: '新年快乐！恭喜发财！' },
      { speaker: 'B', text: 'Happy New Year to you too!', translation: '也祝你新年快乐！' },
      { speaker: 'A', text: 'Here is a red envelope for you.', translation: '这是给你的红包。' },
      { speaker: 'B', text: 'Thank you! May you have good health!', translation: '谢谢！祝你身体健康！' },
    ],
    keywords: ['new year', 'happy', 'red envelope', 'health'],
  },
  {
    id: 'childrens_day',
    title: '儿童节',
    scene: '学校',
    level: 'beginner',
    lines: [
      { speaker: 'Teacher', text: 'Happy Children\'s Day!', translation: '儿童节快乐！' },
      { speaker: 'Students', text: 'Thank you, teacher!', translation: '谢谢老师！' },
      { speaker: 'Teacher', text: 'We have many fun activities today.', translation: '我们今天有很多有趣的活动。' },
      { speaker: 'Student', text: 'I am so excited!', translation: '我好兴奋！' },
    ],
    keywords: ['children day', 'happy', 'activities', 'excited'],
  },
  {
    id: 'christmas_time',
    title: '圣诞节',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Merry Christmas!', translation: '圣诞快乐！' },
      { speaker: 'B', text: 'Merry Christmas! Look at the Christmas tree!', translation: '圣诞快乐！看那棵圣诞树！' },
      { speaker: 'A', text: 'It is beautiful! Are there presents?', translation: '真漂亮！有礼物吗？' },
      { speaker: 'B', text: 'Yes! Let us open them together!', translation: '有！让我们一起打开！' },
    ],
    keywords: ['merry', 'Christmas', 'tree', 'beautiful', 'presents', 'open'],
  },

  // ==================== Grade 3 - Basic Daily Life ====================
  {
    id: 'g3_morning_routine',
    title: '早晨日常',
    scene: '卧室',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'Good morning! Time to get up!', translation: '早上好！该起床了！' },
      { speaker: 'Child', text: 'Good morning, Mom! I am awake now.', translation: '早上好，妈妈！我醒了。' },
      { speaker: 'Mom', text: 'Brush your teeth and wash your face.', translation: '刷牙洗脸。' },
      { speaker: 'Child', text: 'Okay! I will do it now.', translation: '好的！我现在就做。' },
    ],
    keywords: ['morning', 'get up', 'brush', 'teeth', 'face'],
  },
  {
    id: 'g3_getting_dressed',
    title: '穿衣服',
    scene: '卧室',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'What do you want to wear today?', translation: '你今天想穿什么？' },
      { speaker: 'Child', text: 'I want to wear my red shirt.', translation: '我想穿我的红衬衫。' },
      { speaker: 'Mom', text: 'Good choice! Here it is.', translation: '好选择！给你。' },
      { speaker: 'Child', text: 'Thank you, Mom!', translation: '谢谢妈妈！' },
    ],
    keywords: ['wear', 'shirt', 'red', 'choice'],
  },
  {
    id: 'g3_breakfast_time',
    title: '吃早餐',
    scene: '厨房',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'Breakfast is ready!', translation: '早餐好了！' },
      { speaker: 'Child', text: 'What is for breakfast?', translation: '早餐吃什么？' },
      { speaker: 'Mom', text: 'We have eggs, bread and milk.', translation: '我们有鸡蛋、面包和牛奶。' },
      { speaker: 'Child', text: 'Yummy! I like eggs.', translation: '好吃！我喜欢鸡蛋。' },
    ],
    keywords: ['breakfast', 'eggs', 'bread', 'milk', 'yummy'],
  },
  {
    id: 'g3_going_to_school',
    title: '去上学',
    scene: '家门口',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'Do you have your schoolbag?', translation: '你带书包了吗？' },
      { speaker: 'Child', text: 'Yes, I have it.', translation: '是的，我带了。' },
      { speaker: 'Mom', text: 'Have a good day at school!', translation: '祝你今天在学校过得愉快！' },
      { speaker: 'Child', text: 'Thank you! Goodbye!', translation: '谢谢！再见！' },
    ],
    keywords: ['schoolbag', 'school', 'good day', 'goodbye'],
  },
  {
    id: 'g3_classroom_routine',
    title: '课堂日常',
    scene: '教室',
    level: 'beginner',
    lines: [
      { speaker: 'Teacher', text: 'Good morning, class!', translation: '早上好，同学们！' },
      { speaker: 'Students', text: 'Good morning, teacher!', translation: '早上好，老师！' },
      { speaker: 'Teacher', text: 'Sit down, please.', translation: '请坐。' },
      { speaker: 'Students', text: 'Thank you, teacher!', translation: '谢谢老师！' },
    ],
    keywords: ['morning', 'class', 'teacher', 'sit down'],
  },
  {
    id: 'g3_lunch_time',
    title: '午餐时间',
    scene: '学校食堂',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'What do you want for lunch?', translation: '午餐你想吃什么？' },
      { speaker: 'B', text: 'I want rice and vegetables.', translation: '我想要米饭和蔬菜。' },
      { speaker: 'A', text: 'Do you want some soup?', translation: '你想要汤吗？' },
      { speaker: 'B', text: 'Yes, please!', translation: '好的，谢谢！' },
    ],
    keywords: ['lunch', 'rice', 'vegetables', 'soup'],
  },
  {
    id: 'g3_playing_friends',
    title: '和朋友玩耍',
    scene: '操场',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'Let us play together!', translation: '让我们一起玩吧！' },
      { speaker: 'B', text: 'Good idea! What game?', translation: '好主意！什么游戏？' },
      { speaker: 'A', text: 'Let us play tag!', translation: '我们玩抓人游戏吧！' },
      { speaker: 'B', text: 'Okay! I will catch you!', translation: '好的！我会抓住你！' },
    ],
    keywords: ['play', 'together', 'game', 'tag', 'catch'],
  },
  {
    id: 'g3_homework_help',
    title: '作业帮助',
    scene: '家里',
    level: 'beginner',
    lines: [
      { speaker: 'Child', text: 'Mom, can you help me?', translation: '妈妈，你能帮我吗？' },
      { speaker: 'Mom', text: 'Sure! What do you need?', translation: '当然！你需要什么？' },
      { speaker: 'Child', text: 'I need help with my homework.', translation: '我需要作业帮助。' },
      { speaker: 'Mom', text: 'Let me see. I can help you.', translation: '让我看看。我可以帮你。' },
    ],
    keywords: ['help', 'homework', 'need'],
  },
  {
    id: 'g3_bedtime_story',
    title: '睡前故事',
    scene: '卧室',
    level: 'beginner',
    lines: [
      { speaker: 'Child', text: 'Mom, can you tell me a story?', translation: '妈妈，你能给我讲故事吗？' },
      { speaker: 'Mom', text: 'Of course! Which story?', translation: '当然！哪个故事？' },
      { speaker: 'Child', text: 'Tell me about the rabbit.', translation: '给我讲兔子的故事。' },
      { speaker: 'Mom', text: 'Okay. Once upon a time...', translation: '好的。从前……' },
    ],
    keywords: ['story', 'rabbit', 'once upon a time'],
  },
  {
    id: 'g3_sharing_toys',
    title: '分享玩具',
    scene: '家里',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'Can I play with your toy?', translation: '我可以玩你的玩具吗？' },
      { speaker: 'B', text: 'Sure! Here you are.', translation: '当然！给你。' },
      { speaker: 'A', text: 'Thank you! This is fun!', translation: '谢谢！这很好玩！' },
      { speaker: 'B', text: 'I am happy you like it.', translation: '我很高兴你喜欢。' },
    ],
    keywords: ['play', 'toy', 'fun', 'happy'],
  },

  // ==================== Grade 3 - Family and Home ====================
  {
    id: 'g3_family_members',
    title: '家庭成员',
    scene: '客厅',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'Who is that?', translation: '那是谁？' },
      { speaker: 'B', text: 'That is my father.', translation: '那是我的爸爸。' },
      { speaker: 'A', text: 'What does he do?', translation: '他是做什么的？' },
      { speaker: 'B', text: 'He is a teacher.', translation: '他是一名老师。' },
    ],
    keywords: ['who', 'father', 'teacher'],
  },
  {
    id: 'g3_helping_mom',
    title: '帮妈妈',
    scene: '厨房',
    level: 'beginner',
    lines: [
      { speaker: 'Mom', text: 'Can you help me?', translation: '你能帮我吗？' },
      { speaker: 'Child', text: 'Yes! What can I do?', translation: '好的！我能做什么？' },
      { speaker: 'Mom', text: 'Please wash the apples.', translation: '请洗苹果。' },
      { speaker: 'Child', text: 'Okay! I will do it.', translation: '好的！我来做。' },
    ],
    keywords: ['help', 'wash', 'apples'],
  },
  {
    id: 'g3_pet_care',
    title: '照顾宠物',
    scene: '家里',
    level: 'beginner',
    lines: [
      { speaker: 'Dad', text: 'We need to feed the dog.', translation: '我们需要喂狗。' },
      { speaker: 'Child', text: 'Can I do it?', translation: '我可以做吗？' },
      { speaker: 'Dad', text: 'Yes! Here is the food.', translation: '可以！这是食物。' },
      { speaker: 'Child', text: 'Good dog! Eat your food.', translation: '乖狗狗！吃你的食物。' },
    ],
    keywords: ['feed', 'dog', 'food'],
  },

  // ==================== Grade 4 - School Life ====================
  {
    id: 'g4_new_student',
    title: '新同学',
    scene: '教室',
    level: 'beginner',
    lines: [
      { speaker: 'Teacher', text: 'We have a new student today.', translation: '今天我们有一位新同学。' },
      { speaker: 'Student', text: 'Hello! My name is Tom.', translation: '你好！我叫Tom。' },
      { speaker: 'Class', text: 'Welcome to our class!', translation: '欢迎来到我们班！' },
      { speaker: 'Student', text: 'Thank you! Nice to meet you!', translation: '谢谢！很高兴见到你们！' },
    ],
    keywords: ['new', 'student', 'welcome', 'meet'],
  },
  {
    id: 'g4_subjects',
    title: '科目',
    scene: '教室',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'What is your favorite subject?', translation: '你最喜欢的科目是什么？' },
      { speaker: 'B', text: 'I like English best.', translation: '我最喜欢英语。' },
      { speaker: 'A', text: 'Why do you like it?', translation: '你为什么喜欢它？' },
      { speaker: 'B', text: 'Because it is fun!', translation: '因为它很有趣！' },
    ],
    keywords: ['subject', 'English', 'favorite', 'fun'],
  },
  {
    id: 'g4_library_book',
    title: '图书馆借书',
    scene: '图书馆',
    level: 'intermediate',
    lines: [
      { speaker: 'Student', text: 'Excuse me, can I borrow this book?', translation: '打扰一下，我可以借这本书吗？' },
      { speaker: 'Librarian', text: 'Sure! Do you have your library card?', translation: '当然！你有借书卡吗？' },
      { speaker: 'Student', text: 'Yes, here it is.', translation: '有，在这里。' },
      { speaker: 'Librarian', text: 'Please return it in two weeks.', translation: '请在两周内归还。' },
    ],
    keywords: ['borrow', 'book', 'library card', 'return'],
  },
  {
    id: 'g4_art_class',
    title: '美术课',
    scene: '美术教室',
    level: 'beginner',
    lines: [
      { speaker: 'Teacher', text: 'Today we will draw flowers.', translation: '今天我们要画花。' },
      { speaker: 'Student', text: 'What colors should I use?', translation: '我应该用什么颜色？' },
      { speaker: 'Teacher', text: 'You can use red, yellow, and green.', translation: '你可以用红色、黄色和绿色。' },
      { speaker: 'Student', text: 'Okay! I will draw a beautiful flower.', translation: '好的！我要画一朵漂亮的花。' },
    ],
    keywords: ['draw', 'flowers', 'colors', 'red', 'yellow', 'green'],
  },
  {
    id: 'g4_music_class',
    title: '音乐课',
    scene: '音乐教室',
    level: 'beginner',
    lines: [
      { speaker: 'Teacher', text: 'Let us sing a song together.', translation: '让我们一起唱首歌。' },
      { speaker: 'Students', text: 'What song will we sing?', translation: '我们要唱什么歌？' },
      { speaker: 'Teacher', text: 'We will sing "Twinkle Twinkle Little Star".', translation: '我们要唱《小星星》。' },
      { speaker: 'Students', text: 'We love that song!', translation: '我们喜欢那首歌！' },
    ],
    keywords: ['sing', 'song', 'star', 'love'],
  },
  {
    id: 'g4_science_experiment',
    title: '科学实验',
    scene: '科学教室',
    level: 'intermediate',
    lines: [
      { speaker: 'Teacher', text: 'Today we will do an experiment.', translation: '今天我们要做一个实验。' },
      { speaker: 'Student', text: 'What experiment is it?', translation: '是什么实验？' },
      { speaker: 'Teacher', text: 'We will see how plants drink water.', translation: '我们要看看植物怎么喝水。' },
      { speaker: 'Student', text: 'That sounds interesting!', translation: '听起来很有趣！' },
    ],
    keywords: ['experiment', 'plants', 'water', 'interesting'],
  },

  // ==================== Grade 4 - Outdoor Activities ====================
  {
    id: 'g4_park_picnic',
    title: '公园野餐',
    scene: '公园',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'Let us find a good place for our picnic.', translation: '让我们找个好地方野餐。' },
      { speaker: 'Child', text: 'How about under that tree?', translation: '那棵树下怎么样？' },
      { speaker: 'Mom', text: 'Perfect! Let us spread the blanket.', translation: '完美！让我们铺开毯子。' },
      { speaker: 'Child', text: 'I am hungry! Can we eat now?', translation: '我饿了！我们可以吃了吗？' },
    ],
    keywords: ['picnic', 'tree', 'blanket', 'hungry'],
  },
  {
    id: 'g4_zoo_visit',
    title: '参观动物园',
    scene: '动物园',
    level: 'intermediate',
    lines: [
      { speaker: 'Dad', text: 'What animals do you want to see?', translation: '你想看什么动物？' },
      { speaker: 'Child', text: 'I want to see pandas and monkeys!', translation: '我想看熊猫和猴子！' },
      { speaker: 'Dad', text: 'Look! The pandas are eating bamboo.', translation: '看！熊猫在吃竹子。' },
      { speaker: 'Child', text: 'They are so cute!', translation: '它们太可爱了！' },
    ],
    keywords: ['animals', 'pandas', 'monkeys', 'bamboo', 'cute'],
  },
  {
    id: 'g4_bike_riding',
    title: '骑自行车',
    scene: '公园',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Can you ride a bike?', translation: '你会骑自行车吗？' },
      { speaker: 'B', text: 'Yes, I can! Let us ride together.', translation: '是的，我会！让我们一起骑。' },
      { speaker: 'A', text: 'Wait for me! I am slower.', translation: '等等我！我比较慢。' },
      { speaker: 'B', text: 'Do not worry! We can ride slowly.', translation: '别担心！我们可以慢慢骑。' },
    ],
    keywords: ['ride', 'bike', 'slowly', 'wait'],
  },

  // ==================== Grade 5 - Social Interactions ====================
  {
    id: 'g5_birthday_invitation',
    title: '生日邀请',
    scene: '学校',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Are you free this Saturday?', translation: '你周六有空吗？' },
      { speaker: 'B', text: 'Yes, I am. Why?', translation: '有空。怎么了？' },
      { speaker: 'A', text: 'It is my birthday party. Would you like to come?', translation: '是我的生日派对。你想来吗？' },
      { speaker: 'B', text: 'Yes! I would love to come!', translation: '好的！我很乐意参加！' },
    ],
    keywords: ['Saturday', 'birthday', 'party', 'come'],
  },
  {
    id: 'g5_shopping_mall',
    title: '商场购物',
    scene: '商场',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'We need to buy some clothes for you.', translation: '我们需要给你买些衣服。' },
      { speaker: 'Child', text: 'Can I choose them?', translation: '我可以自己选吗？' },
      { speaker: 'Mom', text: 'Of course! Try this one on.', translation: '当然！试试这件。' },
      { speaker: 'Child', text: 'I like this blue shirt!', translation: '我喜欢这件蓝色衬衫！' },
    ],
    keywords: ['buy', 'clothes', 'choose', 'try on', 'shirt'],
  },
  {
    id: 'g5_phone_call',
    title: '打电话',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Hello! May I speak to Amy?', translation: '你好！我可以和Amy说话吗？' },
      { speaker: 'B', text: 'This is Amy speaking. Who is this?', translation: '我是Amy。请问你是谁？' },
      { speaker: 'A', text: 'It is Tom. Are you free tomorrow?', translation: '我是Tom。你明天有空吗？' },
      { speaker: 'B', text: 'Yes, I am. Let us meet at the park.', translation: '有空。我们在公园见面吧。' },
    ],
    keywords: ['speak', 'free', 'tomorrow', 'meet'],
  },
  {
    id: 'g5_seeing_doctor',
    title: '看医生',
    scene: '医院',
    level: 'intermediate',
    lines: [
      { speaker: 'Doctor', text: 'What is wrong?', translation: '怎么了？' },
      { speaker: 'Child', text: 'I have a headache and a fever.', translation: '我头疼发烧。' },
      { speaker: 'Doctor', text: 'Let me check. You have a cold.', translation: '让我检查一下。你感冒了。' },
      { speaker: 'Child', text: 'What should I do?', translation: '我该怎么办？' },
    ],
    keywords: ['wrong', 'headache', 'fever', 'cold'],
  },
  {
    id: 'g5_lost_item',
    title: '丢失物品',
    scene: '学校',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'I cannot find my pencil case!', translation: '我找不到我的铅笔盒了！' },
      { speaker: 'B', text: 'When did you last see it?', translation: '你最后一次看到它是什么时候？' },
      { speaker: 'A', text: 'I had it in the classroom.', translation: '我在教室里还有的。' },
      { speaker: 'B', text: 'Let us go look for it together.', translation: '让我们一起去找吧。' },
    ],
    keywords: ['find', 'pencil case', 'look for'],
  },

  // ==================== Grade 5 - Travel and Transportation ====================
  {
    id: 'g5_train_trip',
    title: '火车旅行',
    scene: '火车站',
    level: 'intermediate',
    lines: [
      { speaker: 'Dad', text: 'We will take the train to Beijing.', translation: '我们要坐火车去北京。' },
      { speaker: 'Child', text: 'How long will it take?', translation: '要多久？' },
      { speaker: 'Dad', text: 'It will take about five hours.', translation: '大约五个小时。' },
      { speaker: 'Child', text: 'I am so excited!', translation: '我好兴奋！' },
    ],
    keywords: ['train', 'Beijing', 'take', 'hours', 'excited'],
  },
  {
    id: 'g5_airport',
    title: '在机场',
    scene: '机场',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'We need to check in first.', translation: '我们需要先办理登机手续。' },
      { speaker: 'Child', text: 'Where is our gate?', translation: '我们的登机口在哪里？' },
      { speaker: 'Mom', text: 'Our gate is number twelve.', translation: '我们的登机口是12号。' },
      { speaker: 'Child', text: 'I can see our plane!', translation: '我能看到我们的飞机了！' },
    ],
    keywords: ['check in', 'gate', 'plane'],
  },
  {
    id: 'g5_subway',
    title: '坐地铁',
    scene: '地铁站',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Which line should we take?', translation: '我们应该坐哪条线？' },
      { speaker: 'B', text: 'We need Line 2 to the city center.', translation: '我们需要坐2号线到市中心。' },
      { speaker: 'A', text: 'How many stops is it?', translation: '有多少站？' },
      { speaker: 'B', text: 'It is five stops from here.', translation: '从这里有五站。' },
    ],
    keywords: ['line', 'city center', 'stops'],
  },

  // ==================== Grade 6 - Future Plans and Dreams ====================
  {
    id: 'g6_future_career',
    title: '未来职业',
    scene: '学校',
    level: 'intermediate',
    lines: [
      { speaker: 'Teacher', text: 'What do you want to be in the future?', translation: '你未来想做什么？' },
      { speaker: 'Student', text: 'I want to be a scientist.', translation: '我想当一名科学家。' },
      { speaker: 'Teacher', text: 'Why do you want to be a scientist?', translation: '为什么你想当科学家？' },
      { speaker: 'Student', text: 'Because I want to help people.', translation: '因为我想帮助人们。' },
    ],
    keywords: ['future', 'scientist', 'help', 'people'],
  },
  {
    id: 'g6_middle_school_prep',
    title: '小升初准备',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'Which middle school do you like?', translation: '你喜欢哪所初中？' },
      { speaker: 'Child', text: 'I want to go to No.1 Middle School.', translation: '我想去第一中学。' },
      { speaker: 'Mom', text: 'You need to study hard then.', translation: '那你需要努力学习。' },
      { speaker: 'Child', text: 'I will do my best!', translation: '我会尽力的！' },
    ],
    keywords: ['middle school', 'study hard', 'best'],
  },
  {
    id: 'g6_summer_plan',
    title: '暑假计划',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'Dad', text: 'What do you want to do this summer?', translation: '这个暑假你想做什么？' },
      { speaker: 'Child', text: 'I want to learn swimming.', translation: '我想学游泳。' },
      { speaker: 'Dad', text: 'That is a good idea! We can also travel.', translation: '好主意！我们还可以去旅行。' },
      { speaker: 'Child', text: 'Can we go to the beach?', translation: '我们可以去海滩吗？' },
    ],
    keywords: ['summer', 'swimming', 'travel', 'beach'],
  },

  // ==================== Grade 6 - Environment and Society ====================
  {
    id: 'g6_environment',
    title: '保护环境',
    scene: '学校',
    level: 'intermediate',
    lines: [
      { speaker: 'Teacher', text: 'Why should we protect the environment?', translation: '为什么我们要保护环境？' },
      { speaker: 'Student', text: 'Because pollution harms animals and plants.', translation: '因为污染伤害动物和植物。' },
      { speaker: 'Teacher', text: 'What can we do?', translation: '我们可以做什么？' },
      { speaker: 'Student', text: 'We can recycle and use less plastic.', translation: '我们可以回收利用并少用塑料。' },
    ],
    keywords: ['environment', 'pollution', 'recycle', 'plastic'],
  },
  {
    id: 'g6_volunteer_work',
    title: '志愿者活动',
    scene: '社区',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'Our class will do volunteer work.', translation: '我们班要做志愿者活动。' },
      { speaker: 'B', text: 'What will you do?', translation: '你们要做什么？' },
      { speaker: 'A', text: 'We will help clean the park.', translation: '我们要帮忙打扫公园。' },
      { speaker: 'B', text: 'That is very meaningful!', translation: '这很有意义！' },
    ],
    keywords: ['volunteer', 'work', 'clean', 'meaningful'],
  },
  {
    id: 'g6_internet_safety',
    title: '网络安全',
    scene: '家里',
    level: 'intermediate',
    lines: [
      { speaker: 'Mom', text: 'You should be careful online.', translation: '你在网上要小心。' },
      { speaker: 'Child', text: 'What should I be careful about?', translation: '我要小心什么？' },
      { speaker: 'Mom', text: 'Do not share personal information.', translation: '不要分享个人信息。' },
      { speaker: 'Child', text: 'I understand. I will be careful.', translation: '我明白了。我会小心的。' },
    ],
    keywords: ['careful', 'online', 'personal', 'information'],
  },

  // ==================== Grade 6 - Advanced Social Situations ====================
  {
    id: 'g6_apologizing',
    title: '道歉',
    scene: '学校',
    level: 'beginner',
    lines: [
      { speaker: 'A', text: 'I am sorry I broke your pen.', translation: '对不起，我弄坏了你的钢笔。' },
      { speaker: 'B', text: 'It is okay. It was an accident.', translation: '没关系。这是个意外。' },
      { speaker: 'A', text: 'I will buy you a new one.', translation: '我会给你买支新的。' },
      { speaker: 'B', text: 'That is very kind of you!', translation: '你真好！' },
    ],
    keywords: ['sorry', 'broke', 'pen', 'accident'],
  },
  {
    id: 'g6_making_suggestions',
    title: '提出建议',
    scene: '学校',
    level: 'intermediate',
    lines: [
      { speaker: 'A', text: 'The classroom is a bit messy.', translation: '教室有点乱。' },
      { speaker: 'B', text: 'Why do not we clean it together?', translation: '我们一起打扫怎么样？' },
      { speaker: 'A', text: 'That is a great idea!', translation: '好主意！' },
      { speaker: 'B', text: 'Let us start right now.', translation: '让我们现在就开始。' },
    ],
    keywords: ['messy', 'clean', 'idea', 'start'],
  },
  {
    id: 'g6_expressing_opinions',
    title: '表达观点',
    scene: '课堂讨论',
    level: 'intermediate',
    lines: [
      { speaker: 'Teacher', text: 'What do you think about the story?', translation: '你觉得这个故事怎么样？' },
      { speaker: 'Student', text: 'I think the story is very moving.', translation: '我觉得这个故事很感人。' },
      { speaker: 'Teacher', text: 'Can you explain why?', translation: '你能解释为什么吗？' },
      { speaker: 'Student', text: 'The characters help each other.', translation: '角色们互相帮助。' },
    ],
    keywords: ['think', 'story', 'moving', 'characters'],
  }
];

// Helper functions
export function getDialogsByLevel(level: 'beginner' | 'intermediate' | 'advanced'): Dialog[] {
  return dialogs.filter(d => d.level === level);
}

export function getDialogById(id: string): Dialog | undefined {
  return dialogs.find(d => d.id === id);
}

export function getRandomDialog(): Dialog {
  return dialogs[Math.floor(Math.random() * dialogs.length)];
}